/* eslint no-mixed-operators: ["error", {"allowSamePrecedence": true}] */
import { pipe, Option } from 'effect';
import { selector } from 'lib/selectors';
import { Set, List, Map } from 'immutable';
import { compose } from 'redux';
import { getRowByNumber, jeSorterCb } from './helper';
import { applyFieldRearrangement } from './fieldRearrangementTransform';
import {
  fieldRearrangementPositionsSelector,
  fieldRearrangementHiddenSelector,
  fieldRearrangementShownSelector,
  fieldRearrangementLockedSelector,
} from 'domain/jeFieldRearrangement/selectors';

import {
  sideEffectFilterColumnCellsFactory,
  sideEffectMakeCellsReadOnly,
  sideEffectRemoveCellStyle,
  selectFirst3Row,
  linesReordering,
  excludeLines,
} from 'domain/reconciliation/helper';

import { denormalize } from './normalizer';

// this can not be imported from reconciliation
const reconciliation = (state) => state.reconciliation;
export const reconcileRequestRowSelector = selector(reconciliation, (j) => j.requestRow);
export const reconcileRequestRowLineIdSelector = selector(reconcileRequestRowSelector, (row) => row.get('id'));
export const reconcileRequestRowLineNumberSelector = selector(reconcileRequestRowSelector, (row) => row.get('row'));

export const journal = (state) => state.journal;
export const journalSelector = selector(journal, (j) => denormalize(j).entry.sort(jeSorterCb));
export const jeNameSelector = selector(journal, (j) => j.jeName);
export const jeTypeSelector = selector(journal, (j) => j.jeType);
export const jeInvoiceTypeSelector = selector(journal, (j) => j.jeInvoiceType);
// Canonical currency identifier of the JE — top-level `jeCurrency` on the API
// response (mirrors `jeInvoiceType`). Used to detect currency changes that must
// invalidate the textract field-binding menu cache (DA-19222).
export const jeCurrencySelector = selector(journal, (j) => j.jeCurrency);
const transactionTypeFieldSelector = selector(journal, (j) => j.transactionTypeField);
export const transactionTypeNameSelector = selector(journalSelector, transactionTypeFieldSelector, (entry, fieldName) =>
  pipe(
    Option.fromNullable(fieldName),
    Option.flatMap((name) => Option.fromNullable(entry.find((cell) => cell.name === name))),
    Option.map((cell) => cell.cellSet),
    Option.flatMap(Option.fromNullable),
    Option.map((cs) => String(cs.display ?? cs.value ?? '')),
    Option.getOrElse(() => ''),
  ),
);
export const isJeLayoutRTLSelector = selector(journal, (j) => j.jeLayout === 'rtl');
export const isStatementSelector = selector(jeTypeSelector, (t) => ['bank_statement', 'reconcile'].includes(t));
export const textMap = (state) => state.mapText;
export const polygonsSelector = selector(textMap, (t) => t.polygons);
export const extraPolygonSelector = selector(textMap, (t) => t.extraPolygon);
export const pagesSelector = selector(textMap, (t) => t.pages);
export const highlightedPolygonsSelector = (page) =>
  selector(pagesSelector, (pages) => pages.get(page)?.highlightedPolygons || new Map());
export const journalEntryHasUserChangesSelector = selector(journal, (j) => j.wasFixed);

export const documentHasHighlightedPolygonsSelector = selector(pagesSelector, (pages) =>
  pages.some((page) => (page.highlightedPolygons?.size ?? 0) > 0),
);

export const referenceSelector = selector(textMap, (t) => t.reference);
export const activeReferenceSelector = selector(referenceSelector, (r) => r.find((v) => v.activity));
export const headerLinesNumberSelector = selector(journal, (j) => j.headerLinesNumber);
export const lineItemsSelector = selector(journal, (j) => j.line_items);
// selects current line_number with offset provided by headers
export const LineItemWithOffsetSelector = (row) =>
  selector(headerLinesNumberSelector, (headerHeight) => row - headerHeight);

export const settingsSelector = selector(journal, (j) => j.settings);
// @to-do for empty lines it should return 6. Max (Settings) + 2 is first line number
// as line_items may be not fullyy loaded after je update
export const firstLineItemSelector = selector(headerLinesNumberSelector, (headerHeight) => headerHeight + 1);
export const headerLineSelector = selector(firstLineItemSelector, (lineNumber) => lineNumber - 1);
export const settingsEntriesSelector = selector(journalSelector, settingsSelector, getRowByNumber);
export const linesSelector = selector(journal, (j) => j.lines);
export const initialHiddenColumnsSelector = selector(journal, (j) => j.initial_hidden_columns);

export const gridEntriesSelector = selector(journalSelector, linesSelector, getRowByNumber);

const summarySelector = selector(journal, (j) => j.summary);
export const summaryEntriesSelector = selector(journalSelector, summarySelector, getRowByNumber);
export const journalWarningsSelector = selector(journal, (j) => j.messages);
export const journalPublicationWarningsSelector = selector(journal, (j) => j.publicationWarnings);

export const errorFromErpSelector = selector(journal, (j) => j.errorFromErp);
export const lineCountSelector = selector(journal, (j) => j.lineCount);
export const selectedRowSelector = selector(journal, (j) => j.selectedRow);
export const reconcileColumnSelector = selector(journal, (j) => j.reconcileColumn);
export const pinnedColumnSelector = selector(journal, (j) => j.pinnedColumn);
export const notHiddenColumnsSelector = selector(journal, (j) => j.notHiddenColumns);
export const amountColumnSelector = selector(journal, (j) => j.amountColumn);
export const rowMessagesSelector = selector(journal, (j) => j.rowMessages);
export const lineItemsDataSelector = selector(journal, (j) => j.lineItemsData);
export const jeGridLineColumnsDictionarySelector = selector(journal, (j) => j.jeGridLineColumnsDictionary);

const paginationSelector = selector(journal, (j) => j.paginationList);
export const paginationListSelector = selector(paginationSelector, (p) => p.list);
export const paginationListPageTokenSelector = selector(paginationSelector, (p) => p.pageToken);
const cachedPaginationListsSelector = selector(paginationSelector, (p) => p.cachedList);
export const getCachedPaginationListSelector = selector(cachedPaginationListsSelector, (cl) => (id) => cl.get(id));

// data for transaction request
const lineGroupSelector = selector(journalSelector, (j) => j.groupBy((g) => g._row));

export const clearSelectedRowsWithoutSelector = selector(
  selectedRowSelector,
  lineGroupSelector,
  reconcileColumnSelector,
  (rows, gr, col) => excludeLines(gr, rows, col, ['Ignored', 'Reconciled']),
);

export const selectedLinesEntriesSelector = selector(
  lineGroupSelector,
  clearSelectedRowsWithoutSelector,
  headerLineSelector,
  (gr, rows, header) => linesReordering(gr, selectFirst3Row(rows).unshift(header)),
);
export const singleLineEntriesSelector = selector(
  lineGroupSelector,
  reconcileRequestRowLineNumberSelector, // single row user clicked request icon for
  headerLineSelector,
  (list, lineNumber, header) => (lineNumber ? linesReordering(list, List([header, lineNumber])) : Map()),
);
export const selectedLineIdsSelector = selector(
  clearSelectedRowsWithoutSelector,
  reconcileRequestRowLineNumberSelector,
  lineItemsDataSelector,
  (rows, singleRow, linesData) =>
    linesData
      // singleRow indicates we selected single line, so ignore selection if present
      // and filter single line data only
      .filter((V, K) => (singleRow ? singleRow === parseInt(K, 10) : rows.has(parseInt(K, 10))))
      .reduce((A, V) => A.push(V.line_id), new List()),
);
export const headerLineEntriesSelector = selector(journalSelector, headerLineSelector, (entry, line) =>
  getRowByNumber(entry, Set([line])),
);
export const selectedLinesWithHeaderAsListSelector = selector(
  selectedLinesEntriesSelector,
  singleLineEntriesSelector,
  headerLineEntriesSelector,
  (linesEntriesList, singleLineEntriesList) =>
    // if singleLineEntriesList is not empty, we have request line icon clicked, so use single line data
    singleLineEntriesList.size > 0 ? singleLineEntriesList : linesEntriesList,
);

export const selectedLinesFlatSelector = selector(
  selectedLinesWithHeaderAsListSelector,
  reconcileColumnSelector,
  (list, reconcileColumn) =>
    compose(
      sideEffectRemoveCellStyle, // removes header styles also
      sideEffectMakeCellsReadOnly,
      sideEffectFilterColumnCellsFactory(reconcileColumn),
      // sideEffectFlatternJeList,
    )(list),
);

const journalPaginationListsSelector = selector(journalSelector, (j) =>
  j.filter((e) => e.type === 'paginated_list' || e.type === 'paginated_multiselect_list'),
);

export const cachedPaginationListsIncludeIdsSelector = selector(cachedPaginationListsSelector, (cl) =>
  cl.reduce((a, v) => [...a, v.include_id], []),
);

export const journalNotCachedPaginationListsSelector = selector(
  journalPaginationListsSelector,
  cachedPaginationListsIncludeIdsSelector,
  (entries, cachedIds) => entries.filter((e) => e.include_id && !cachedIds.includes(e.include_id)),
);

export const journalSettings = (state) => state.journalSettings;
export const journalSettingsPinnedColsSelector = selector(journalSettings, (j) => j.pinnedCols);
export const journalSettingsDisabledColsSelector = selector(journalSettings, (j) => j.disabledCols);
export const journalSettingsOrderedColsSelector = selector(journalSettings, (j) => j.orderedCols);

export const jeMatchedLinesSelector = selector(journal, (j) => j.matchedLines);

export const jeMatchedLineNumbersSelector = selector(jeMatchedLinesSelector, (lines) => {
  const res = lines
    .reduce(
      (acc, matchedLines, lineItemIndex) =>
        acc.set(
          lineItemIndex,
          matchedLines.map((line) => line.get('line', null)),
        ),
      new Map(),
    )
    .filter((list) => list && list.size > 0);
  return res;
});

// returns absolute line numbers e.g. 12,13,16
export const jeUnmatchedLineNumbersSelector = selector(
  jeMatchedLinesSelector,
  lineItemsSelector,
  (matchedLines, lineItems) => {
    const matchedLineNumbers = matchedLines
      .entrySeq()
      .flatMap(([key, items]) =>
        items.filter((item) => ['match', 'partial_match'].includes(item.get('status'))).map(() => parseInt(key, 10)),
      )
      .toSet()
      .toArray();

    // select only line items that are NOT at the matched indices (convert 1-based to 0-based)
    const unmatchedLineNumbers = lineItems.toArray().filter((_, index) => !matchedLineNumbers.includes(index + 1));

    return Set(unmatchedLineNumbers);
  },
);

export const isJELoadingSelector = selector(journal, (j) => j.loading);
export const isJEEditingSelector = selector(journal, (j) => j.editing);

export const textractForVendorEnabledSelector = selector(journal, (j) => j.textractForVendorEnabled);

/**
 * Settings entries with field rearrangement config applied.
 * Used by JEHeadersTable for the regular view: rearranged cells appear at their
 * target positions; hidden cells are removed from the OrderedMap (keys absent,
 * not null placeholders).
 */
export const rearrangedSettingsSelector = selector(
  settingsEntriesSelector,
  fieldRearrangementPositionsSelector,
  fieldRearrangementHiddenSelector,
  fieldRearrangementShownSelector,
  fieldRearrangementLockedSelector,
  applyFieldRearrangement,
);
